import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function PriceHistoryChart({ history, currency }) {
  const chartWidth = 640;
  const chartHeight = 220;
  const points = useMemo(() => {
    if (!history || history.length === 0) return [];
    const sorted = [...history].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    const values = sorted.map((item) => Number(item.price) || 0);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const padding = 24;
    const step = sorted.length > 1 ? (chartWidth - padding * 2) / (sorted.length - 1) : 0;

    return sorted.map((item, idx) => {
      const price = Number(item.price) || 0;
      const x = padding + idx * step;
      const y =
        padding +
        (chartHeight - padding * 2) * (1 - (price - min) / range);
      return {
        x,
        y,
        label: new Date(item.timestamp).toLocaleDateString('ru-RU'),
      };
    });
  }, [history]);

  if (!points.length) return null;

  const pathD = points
    .map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
  const areaPath = `${pathD} L ${points[points.length - 1].x} ${chartHeight} L ${points[0].x} ${chartHeight} Z`;

  return (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="text-sm text-gray-600 mb-2">График изменения цены ({currency || 'KZT'})</div>
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-56">
        <defs>
          <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={pathD} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinejoin="round" />
        <path d={areaPath} fill="url(#priceGradient)" opacity="0.7" />
        {points.map((point, idx) => (
          <g key={`${point.label}-${idx}`}>
            <circle cx={point.x} cy={point.y} r="4" fill="#1d4ed8" stroke="#fff" strokeWidth="1.5" />
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function PriceHistoryModal({ product, onClose }) {
  if (!product) return null;

  const formatCurrency = (value, currency) => {
    const rounded = Math.round(value || 0);
    return `${rounded.toLocaleString('ru-RU')} ${currency || 'KZT'}`;
  };

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>История цен: {product.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <PriceHistoryChart
            history={product.price_history || []}
            currency={product.supplier_currency}
          />
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-600 font-medium">Дата</th>
                    <th className="text-right px-3 py-2 text-gray-600 font-medium">Цена</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[...(product.price_history || [])]
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((entry, idx) => (
                      <tr key={`${entry.timestamp}-${idx}`}>
                        <td className="px-3 py-2 text-gray-700">
                          {new Date(entry.timestamp).toLocaleString('ru-RU')}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900">
                          {formatCurrency(entry.price, product.supplier_currency)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
